import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { addBreadcrumb } from "@sentry/react";

import { postLoginPath } from "@lib/authentication/loginRedirect";
import { clearSimAssetAuthorizations } from "@lib/simulation/simAssetCredentialStore";
import { clearSentryUser } from "@utils/sentry";

import { isAuthError } from "../../amplify/amplify.helpers";
import {
  AuthenticationErrors,
  IdentityProviderApiErrors,
} from "../../amplify/identity-provider.enum";
import {
  identityProviderMapError,
  isIdentityProviderError,
} from "../../identity-provider-error";
import { Auth } from "../../isomorphic-auth/isomorphic-auth";

import type {
  ConfirmResetPasswordProps,
  SignInProps,
  UpdatePasswordProps,
} from "./use-authentication.types";

import { queryClient } from "@/utils/QueryClientProvider";

/** Amplify v6 refuses signIn outright while it holds tokens. Members reach
 *  the login form in that state when the page decided they were signed out
 *  but Amplify did not: a reset link that signed them in from another tab, a
 *  post-purchase sign-in that finished after checkout gave up on it, a token
 *  refresh that failed only on the network (OMG-2638). */
const isAlreadySignedIn = (error: unknown) =>
  isAuthError(error) &&
  error.name === IdentityProviderApiErrors.USER_ALREADY_AUTHENTICATED;

/** The pool signs in by email, case-insensitively (signInCaseSensitive:
 *  false), so this is Cognito's own rule for "the same account". */
const isSameAccount = (sessionEmail: string | undefined, typedEmail: string) =>
  !!sessionEmail &&
  sessionEmail.trim().toLowerCase() === typedEmail.trim().toLowerCase();

export const useAuthentication = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  // Honor a ?redirect= put on /login by RequireAuth so a deep link survives
  // the login wall; otherwise /home as before (OMG-2449).
  const navigateAfterSignIn = () =>
    navigate(postLoginPath(location.search), {
      state: { fromLogin: true },
    });

  const signIn = async ({ email, password }: SignInProps) => {
    try {
      let result = await Auth.signIn({ username: email, password });

      if (!result.success && isAlreadySignedIn(result.error)) {
        const session = await Auth.currentAuthenticatedUser();
        if (
          session.success &&
          isSameAccount(session.data.authenticatedUser.email, email)
        ) {
          // The live session is the account they typed. This browser can
          // already open it without a password, so going there grants nothing
          // new, and a sign-out-and-retry would only add a way to fail.
          addBreadcrumb({
            category: "auth",
            message: "signIn: continuing the typed account's live session",
            level: "info",
          });
          return navigateAfterSignIn();
        }

        // Another account (a shared device), or a session that can no longer
        // be read. Never continue as whoever holds it: sign it out with the
        // usual shared-device cleanup, drop its cached member data (the
        // menu's sign-out gets that from a full page load), and try the typed
        // credentials once more. A second refusal falls through to the throw.
        addBreadcrumb({
          category: "auth",
          message:
            "signIn: signing out a session that is not the typed account",
          level: "warning",
          data: { sessionReadable: session.success },
        });
        await signOut();
        // Queries only: RedeemForm calls signIn from inside its own mutation.
        queryClient.removeQueries();
        result = await Auth.signIn({ username: email, password });
      }

      if (result.success) {
        // Amplify v6 does NOT throw PasswordResetRequiredException for a
        // RESET_REQUIRED user (e.g. the OMG-2076 bulk import): it swallows
        // the Cognito error into a "successful" sign-in result with a
        // RESET_PASSWORD next step and isSignedIn=false. Without this check
        // we'd navigate to /home unauthenticated and bounce back to /login.
        // Surface it as the canonical error so callers keep one code path
        // for RESET_REQUIRED users (reset-on-return flow, OMG-2077).
        if (result.data?.nextStep?.signInStep === "RESET_PASSWORD") {
          throw new Error(AuthenticationErrors.PASSWORD_RESET_REQUIRED);
        }

        return navigateAfterSignIn();
      }

      throw result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const result = await Auth.signOut();

      if (result.success) {
        // The simulation asset policy is cached in module scope keyed by asset
        // version only, so without this the next member on a shared device would
        // ride this member's signature for up to 12 h — and since OMG-2277's API
        // half gates on entitlement, that would carry the entitlement with it.
        clearSimAssetAuthorizations();
        return clearSentryUser();
      }

      throw result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const result = await Auth.resetPassword({
        username: email,
        // The Cognito CustomMessage Lambda (omgyes-infra customPasswordReset)
        // localises the reset email from this; without it every member got
        // the English email (OMG-2639). resolvedLanguage, never
        // i18n.language: only the former is constrained to supportedLngs, so
        // a crafted ?lng= cannot reach the Lambda's translations URL.
        options: {
          clientMetadata: { locale: i18n.resolvedLanguage || "en" },
        },
      });

      if (result.success) {
        return;
      }

      throw result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  const confirmResetPassword = async ({
    email,
    confirmationCode,
    newPassword,
  }: ConfirmResetPasswordProps) => {
    try {
      const result = await Auth.confirmResetPassword({
        username: email,
        confirmationCode,
        newPassword,
      });

      if (result.success) {
        return;
      }

      throw result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  const updatePassword = async (props: UpdatePasswordProps) => {
    try {
      const result = await Auth.updatePassword(props);

      if (result.success) {
        return;
      }

      throw result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  return {
    signIn,
    signOut,
    resetPassword,
    confirmResetPassword,
    updatePassword,
  };
};
